import type { CmsInfo, Response, Tags } from '~/types'
import { getNavItemTreeByNavIndex, getCategoryByUrl, getTagsListByUrl, getSinglePageByUrl, getCategoryDetailByArticleFullUrl, getChildCategoryIds, getChildTagsIds, handleTree, appendCategoryUrl, isEmpty } from '~/utils/serverApiAndNuxtApi/nuxtCore'

// 存储组件名称列表
const cmsComponentsNameList: string[] = []

/**
 * 客户端缓存: 多个组件可能在同一次切换中并发调用 getCmsInfo (e.g. GameCard 列表)
 * 用 globalThis 做内存级去重, 避免 N 个 GameCard 触发 N 次请求
 */
let cmsInfoClientPromise: Promise<CmsInfo> | null = null

/**
 * 获取 CMS 信息 - 通过 API 获取
 *
 * 核心优化：
 * 1. 使用 $fetch 而不是 useFetch，避免序列化到 __NUXT_DATA__
 * 2. 服务端缓存由 server/service/javaService.ts 管理（5分钟 TTL）
 * 3. 客户端由 Cache-Control: max-age=300 + globalThis Promise 去重
 *
 * ⚠️ 重要：此函数不使用 useAsyncData，因此不会序列化到 HTML
 */
export const getCmsInfo = async (): Promise<CmsInfo> => {
    if (import.meta.client && cmsInfoClientPromise) {
        return cmsInfoClientPromise
    }
    const promise = (async () => {
        const response = await $fetch('/api/getCmsInfo', { method: 'POST' }) as Response<CmsInfo>
        return response.data
    })()
    if (import.meta.client) {
        cmsInfoClientPromise = promise
    }
    return promise
}

const checkComponentNameExist = (name: string) => {
    // 如果name为空，则认为组件存在,就是Default组件
    return cmsComponentsNameList.includes(name)
}

/**
 * 构建文章的访问 URL
 * 用于 GameCard 等子组件的 href, 避免子组件内部调用 getCmsInfo
 *
 * 优先级:
 *  1. article.fullUrl (后端预填的)
 *  2. 根据 article.categoryId 在 cmsInfo.category 中查找 category.url
 *  3. 兜底: /games/{titleUrl}
 */
const buildArticleUrl = (article: any, cmsInfo: CmsInfo | null): string => {
    if (!article) return '/'
    if (article.fullUrl) return article.fullUrl

    const categoryId = article.categoryId
    let cat: any = null
    if (categoryId && cmsInfo?.category) {
        cat = cmsInfo.category.find((c: any) => c.id === categoryId)
    }
    const catUrl = cat?.url || 'games'
    const titleUrl = article.titleUrl || article.id
    return `/${catUrl}/${titleUrl}`
}

/**
 * 根据起始标签 id，在站点标签树中向上追溯祖先链，生成面包屑路径。
 *
 * 用于文章/游戏页面的面包屑展示：从文章关联的某个标签出发，沿 `pid` 逐层向上，
 * 返回从最顶层【非根】祖先到直接标签的有序数组（根标签 isRoot=true 会被跳过，
 * 因为根标签只是顶层容器，不适合作为面包屑层级）。
 *
 * @param tagId 起始标签 id（通常取 article.tagsList[0].id）
 * @param tags 站点的完整标签列表（cmsInfo.tags）
 * @returns 从最顶层非根祖先到起始标签的有序 Tags 数组
 */
export const getTagBreadcrumbChain = (tagId: number, tags: Tags[]): Tags[] => {
    const chain: Tags[] = []
    let currentId: number | null = tagId
    while (currentId != null && currentId > 0) {
        const tag = tags.find((t: any) => t.id === currentId)
        if (!tag) break
        // Skip root tags (isRoot === true) — they are just top-level containers,
        // not meaningful breadcrumb segments.
        if (!(tag as any).isRoot) {
            chain.unshift(tag)
        }
        currentId = (tag as any).pid ?? null
    }
    return chain
}

/**
 * 初始化函数
 */
;(function initCmsComponentsNameList() {
    // 这里不能直接使用import.meta.cmsComponents.forEach，因为forEach是同步的，而import.meta.cmsComponents是异步的，所以会报错
    const config = import.meta.cmsComponents
    config!.forEach((item: string) => {
        const tempName = item.split('/template/')[1]!.replace('.vue', '')
        let componentName = ''
        tempName.split('/').forEach((name: string) => {
            // Convert kebab-case to PascalCase: "product-bag" → "ProductBag"
            const pascalName = name
                .split('-')
                .map(part => part.charAt(0).toUpperCase() + part.slice(1))
                .join('')
            componentName += pascalName
        })
        cmsComponentsNameList.push(componentName)
    })
})()

export default {
    // 这个函数式只能给nuxt组合API这个文件自己写的
    getCmsInfo,

    // 这些函数是组合API和ServerApi(nitro)都可以使用的,是重cmsCore导入进来的。
    getChildCategoryIds,
    getChildTagsIds,
    getNavItemTreeByNavIndex,
    getCategoryByUrl,
    getTagsListByUrl,
    getSinglePageByUrl,
    checkComponentNameExist,
    getCategoryDetailByArticleFullUrl,
    buildArticleUrl,
    getTagBreadcrumbChain,


    // 这些函数是组合API和ServerApi(nitro)都可以使用的,是重cmsCore导入进来的
    handleTree,
    appendCategoryUrl,
    isEmpty,
}
